/** @file Utilities and helpers to deal with Vue. */
import { detectOS } from 'detect-browser';
import { identity } from 'purify-ts';
import Vue from 'vue';
import { Maybe } from '@codegrade/maybe';
import { array, assert, errors, helpers, typeHelpers } from '@codegrade/utils';
import { range } from './range';
/** @returns A fresh empty VNode. */
export function emptyVNode() {
    return { isRootInsert: false, isComment: true, text: '' };
}
/**
 * Renders a scoped slot with a fallback and an optional mapper function.
 *
 * @param vm The Vue instance or component-like object.
 * @param name The name of the slot to render.
 * @param scope The scope data to pass to the slot.
 * @param mapper A function to transform the rendered nodes from the slot. This function is only
 * applied if the slot exists and renders content. It receives the rendered node(s) and should
 * return the node(s) to use. Defaults to an identity function (returns the input node as is).
 * @param dflt A function that returns the default content to render if the slot is not found or
 * returns no content.  It receives the scope data. Defaults to a function that returns an empty
 * VNode.
 * @returns The rendered VNode(s) from the slot, or the default content, potentially transformed by
 * the mapper.
 */
export function renderSlot(vm, name, scope, mapper = identity, dflt = emptyVNode) {
    return Maybe.fromNullable(vm.$scopedSlots[name])
        .chainNullable(slot => slot(scope))
        .map(mapper)
        .orDefaultLazy(() => dflt(scope));
}
/**
 * Attempts to render a scoped slot using the provided scope, if the slot exists.
 * If the slot does not exist or returns `undefined`, it returns `Maybe.Nothing`.
 *
 * @template T The type of the scope data to pass to the slot.
 * @param scope The data to pass to the scoped slot as its context, containing the
 * necessary data in order to render the slot.
 * @param slot The scoped slot function to render. This function receives the `scope`
 * as its argument and should return a `NodeLike` or `undefined`.
 * @returns A `Maybe` containing the rendered `NodeLike` if the slot exists and produces
 * a value, or `Nothing` if the slot is undefined or returns `undefined`.
 */
export function maybeRenderScopedSlot(scope, slot) {
    if (slot != null) {
        return Maybe.of(slot(scope));
    }
    return Maybe.empty();
}
/**
 * Renders a scoped slot using the provided scope, or falls back to a default
 * content generator if the slot does not exist or returns `undefined`.
 *
 * @template T The type of the scope data to pass to the slot.
 * @template Y The type of the default content returned when the slot is undefined
 * or produces no output.
 * @param scope The data to pass to the scoped slot as its context.
 * @param slot The scoped slot function to render. This function receives the `scope`
 * as its argument and should return a `NodeLike` or `undefined`.
 * @param dflt A fallback function to generate default content. This function receives
 * the `scope` as its argument and must return the fallback content of type `Y`.
 * @returns The rendered `NodeLike` from the slot if it exists and produces content, or
 * the fallback content generated by `dflt` if the slot is undefined or returns `undefined`.
 */
export function renderScopedSlot(scope, slot, dflt) {
    const res = maybeRenderScopedSlot(scope, slot);
    return res.orDefaultLazy(() => dflt(scope));
}
/**
 * Sets a value on an existing, writable property, ensuring it is reactive.
 *
 * @param obj The object.
 * @param key The key of the property to set. Must be a writable key of the object.
 * @param value The value to set, matching the property's type.
 */
export function vueSet(obj, key, value) {
    // Do not return the value from `Vue.set` as `Vue.set` might make `value` work for reactivity,
    // but it returns the input for `value`. Do not return `obj[key]` either, as this extra read
    // isn't always required.
    Vue.set(obj, key, value);
    // If we don't do this read some nested objects don't trigger properly. A good example of this
    // is the editor sessions. The reason for this is unknown, and difficult to understand. You are
    // free to investigate this further, but you should update the wasted hours timer here:
    // Wasted hours: 3
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = obj[key];
}
/**
 * Sets a default value on an existing, writable property only if it doesn't already exist.
 *
 * @param obj The object.
 * @param key The key of the property to set. Must be a writable key.
 * @param value The default value to set.
 * @returns The new value if set, or the existing value.
 */
export function vueSetDefault(obj, key, value) {
    if (!Object.hasOwn(obj, key)) {
        vueSet(obj, key, value);
    }
    return obj[key];
}
/**
 * Sets a property if the value is Just, otherwise deletes the property.
 *
 * The key must be writable.
 *
 * @param obj The object.
 * @param key The key of the property to set or delete. Must be a writable key.
 * @param value A Maybe containing the value.
 */
export function vueSetOrDelete(obj, key, value) {
    if (value.isJust()) {
        Vue.set(obj, key, value.value);
    }
    else {
        Vue.delete(obj, key);
    }
}
/**
 * Deletes a property from an object. The key must be writable.
 *
 * @param obj The object.
 * @param key The key of the property to delete. Must be a writable key.
 */
export function vueDelete(obj, key) {
    Vue.delete(obj, key);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function innerRenderRow(h, columns, opts) {
    // Make sure that for every combination of rows we are actually
    // able to find a correct bootstrap class.
    if (!IS_PRODUCTION) {
        const renderRowOptsKeys = typeHelpers.keysOfFast(['amountDefault', 'amountXLarge', 'amountLarge', 'amountMedium']);
        assert.AssertionError.assert(renderRowOptsKeys.every(amountPerDim => range(1, Math.min(columns.length, opts[amountPerDim] ?? 1) + 1).every(amount => Math.floor(12 / amount) === 12 / amount)));
    }
    const cols = Maybe.catMaybes(columns);
    if (cols.length === 0) {
        return Maybe.empty();
    }
    const amountCols = cols.length;
    const amountDefault = Math.min(amountCols, opts.amountDefault ?? 1);
    const amountMedium = Math.min(amountCols, opts.amountMedium ?? amountDefault);
    const amountLarge = Math.min(amountCols, opts.amountLarge ?? amountMedium);
    const amountXLarge = Math.min(amountCols, opts.amountXLarge ?? amountLarge);
    const colOpts = [
        ['md', amountMedium],
        ['lg', amountLarge],
        ['xl', amountXLarge],
    ].reduce((acc, [variant, amount]) => {
        if (array.last(acc)[1] !== amount) {
            acc.push([variant, amount]);
        }
        return acc;
    }, [[undefined, amountDefault]]);
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const join = (...args) => args.filter(x => x != null).join('-');
    const baseClasses = colOpts.map(([variant, amount]) => join('col', variant, Math.floor(12 / amount).toString(10)));
    // Add margins to all divs that are not on the last row.
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const getMargins = (idx) => colOpts.map(([variant, amount]) => {
        const marginAmount = idx + amount < amountCols ? 3 : 0;
        return join('mb', variant, marginAmount.toString(10));
    });
    return Maybe.of(cols.map((col, idx) => {
        const classes = [baseClasses, getMargins(idx)];
        return h('div', { class: classes }, [col]);
    }));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function renderRow(h, columns, opts) {
    return innerRenderRow(h, columns, opts).map(content => h('div', { class: 'row mb-3' }, content));
}
// Tabindices are global across the page, so this gives out bases for the
// tabindex in chunks of 1000. If a component needs more than 1000 tabindices it
// should request multiple bases.
const nextTabindex = helpers.makeUniqueCounter();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function getTabindexBase() {
    return 1000 * nextTabindex();
}
/** @returns Whether it's a Mac or not. */
export function isMacDevice() {
    try {
        const os = detectOS(navigator.userAgent);
        return os === 'Mac OS' || os === 'iOS';
    }
    catch {
        // DO NOTHING...
    }
    return false;
}
/** Check whether the Ctrl or Cmd key were pressed. */
export const isCtrlOrCmd = (() => {
    // We will handle the meta key on Mac or if the OS could not be determined.
    const shouldHandleMeta = isMacDevice();
    return (event) => {
        if (event.ctrlKey) {
            return true;
        }
        if (event.metaKey) {
            return shouldHandleMeta;
        }
        return false;
    };
})();
/**
 * Call an event handler for a KeyboardEvent only when either ctrl or cmd was
 * pressed in combination with any of the keys in the `key` argument. The
 * values in `key` must be those in the first column of the table at
 * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values.
 *
 * @param key The key to check for in combination with Ctrl or Cmd.
 * @param handler The event handler to call if the requested key was pressed in
 * combination with Ctrl or Cmd.
 * @returns An event handler that will only run if the requested key was pressed
 * in combination with Ctrl or Cmd.
 */
export function ctrlOrCmd(key, handler) {
    return (event) => {
        if (isCtrlOrCmd(event) && event.key === key) {
            return handler(event);
        }
        return undefined;
    };
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function doubleRequestAnimationFrame(cb) {
    return new Promise(resolve => {
        Vue.nextTick(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (cb != null) {
                        cb();
                    }
                    resolve();
                });
            });
        });
    });
}
/**
 * Useful when doing async requests in a vue component. This function calls the
 * provided `getter` to store some data in its closure once its called. It
 * returns a function that accepts yet another function `fun` and arguments for
 * `fun` and, when called, will call the `getter` again and only call `fun` with
 * the provided arguments if the data returned by `getter` was unchanged between
 * the calls to it.
 *
 * @param getter The getter for the data that might change.
 * @returns A function that will only be called when the return value of
 * `getter` was unchanged.
 */
export function ifDataUnchanged(getter) {
    const initialData = array.ensureArray(getter());
    return fun => 
    /**
     * A function that calls `fun` if the data is unchanged.
     *
     * @param args Arguments passed on to the wrapped function.
     */
    (...args) => {
        const newData = array.ensureArray(getter());
        if (initialData.length === newData.length &&
            initialData.every((el, i) => newData[i] === el)) {
            fun(...args);
        }
    };
}
/**
 * Focus on the provided page element.
 *
 * It expected the element (or the backing element) to be a `HTMLElement`.
 *
 * @param el The element to focus on.
 */
export function focusOn(el) {
    // the `$withRef` method returns a `Element | Vue`. In our usage of
    // the method, we always expect it to be `HTMLElement | Vue`. The
    // same goes for `el.$el`.
    if (el instanceof Element) {
        el.focus();
    }
    else {
        el.$el.focus();
    }
}
/**
 * Observes changes in specified properties of a Vue instance, triggering an asynchronous loader
 * function whenever the properties change. Manages loading state and error handling based on the
 * function's results.
 *
 * This function handles concurrent updates correctly, and only causes errors or results from the
 * last load to be set.
 *
 * This method should only be used in the `created` hook of a Vue instance.
 *
 * @param root0 The options, see the interface for the meaning of each field.
 *
 * @example
 * watchedLoad({
 *     instance: this,
 *     getValues() { return { userId: this.userId }; },
 *     async load(data, opts) {
 *         if (opts.isRetry) console.log('Retrying...');
 *         return await fetchUserData(data.userId);
 *     },
 *     reset() {
 *         this.userData = Maybe.empty();
 *         this.error = Maybe.empty();
 *     },
 *     setError(error) {
 *         this.error = Maybe.of(error);
 *     },
 *     setResult(result) {
 *         this.userData = Maybe.of(result);
 *     },
 *     onDropped(result) {
 *         console.log("Dropped result:", result);
 *     }
 * });
 */
export function watchedLoad({ instance, getValues, load, reset, setError, setResult, onDropped, }) {
    /** @returns The watched value, serialized to be a string. */
    function watcher() {
        const result = getValues.call(this);
        return JSON.stringify(result);
    }
    let callbackCnt = 0;
    let destroyed = false;
    if (onDropped != null) {
        instance.$on('hook:destroyed', () => {
            destroyed = true;
        });
    }
    instance.$watch(watcher, async function callback(newValue, _, isRetry = false) {
        reset.call(this);
        callbackCnt += 1;
        const curCnt = callbackCnt;
        try {
            const parsed = JSON.parse(newValue);
            const loaded = await load(parsed, {
                hasChanged: () => curCnt !== callbackCnt,
                isRetry,
                retry: () => {
                    if (curCnt === callbackCnt) {
                        callback.call(this, watcher.call(this), '', true);
                    }
                },
            });
            if (!destroyed && curCnt === callbackCnt) {
                setResult.call(this, loaded);
            }
            else {
                onDropped?.(loaded);
            }
        }
        catch (caught) {
            if (curCnt === callbackCnt) {
                setError.call(this, {
                    error: errors.coerceToError(caught),
                    retry: () => {
                        if (curCnt === callbackCnt) {
                            callback.call(this, watcher.call(this), '', true);
                        }
                    },
                });
            }
        }
    }, {
        immediate: true,
    });
}
/**
 * Observes changes in specified properties of a Vue instance and triggers an asynchronous loader
 * function whenever these properties change. Automatically resets the specified error field and sets
 * errors to this field if the load operation fails.
 *
 * This function handles concurrent updates correctly, ensuring that only the latest error is set.
 *
 * This method should only be used in the `created` hook of a Vue instance.
 *
 * @param opts The options, see the interface for the meaning of each field.
 *
 * @example
 * watchedLoadNoResult({
 *     instance: this,
 *     errorField: 'error',
 *     getValues() { return { userId: this.userId }; },
 *     async loader(data) {
 *         await fetchUserData(data.userId);
 *     }
 * });
 */
export function watchedLoadNoResult(opts) {
    const errorField = opts.errorField;
    watchedLoad({
        ...opts,
        setResult: () => { },
        /** Reset the error field. */
        reset() {
            this[errorField] = Maybe.empty();
        },
        /**
         * Set the error field.
         *
         * @param error The error to set.
         */
        setError(error) {
            this[errorField] = Maybe.of(error);
        },
    });
}
/**
 * A watchedLoad with an simplified interface.
 *
 * Use when the reset, result, and error handlers are not required.
 *
 * @param opts Options for a watched pre-load operation.
 */
export function watchedPreload(opts) {
    watchedLoad({
        ...opts,
        setResult: () => { },
        /** Don't do anything, we are preloading. */
        reset: () => { },
        /**
         * Called when pre-loading fails.
         *
         * @param error The error of pre-loading.
         */
        setError(error) {
            // eslint-disable-next-line no-console
            console.log(`Failed to load ${opts.whatToLoad}`, error.error);
        },
    });
}
/**
 * Rounding method to ensure positional values are optimal for the screen.
 *
 * This function scales the input value by the current `window.devicePixelRatio`,
 * rounds it to the nearest integer, and then scales it back down. This ensures
 * that values are aligned to the pixel grid of a given screen.
 *
 * See: https://floating-ui.com/docs/misc#subpixel-and-accelerated-positioning for more info.
 *
 * @param value Incoming value.
 * @returns Rounded value.
 */
export function roundByDPR(value) {
    const dpr = window.devicePixelRatio ?? 1;
    return Math.round(value * dpr) / dpr;
}
/**
 * This utility helps determine if a given input is a Vue component instance.
 *
 * @param input Any value that should be checked.
 * @returns A boolean value, true when the input is a Vue instance.
 */
export function isVueComponent(input) {
    // eslint-disable-next-line no-underscore-dangle -- This is from Vue so ESLint must not complain!
    return Object.hasOwn(input, '_isVue') && input._isVue === true;
}
/**
 * The utility takes a value from vue.$refs and try its best to return an element. If a Vue instance
 * is passed in, then its $el is returned.
 *
 * Note 1: This utility does not help you differentiate between Element and HTMLElement, it focuses
 * on differentiate Vue instance and elements.
 *
 * @example Example for dealing with SVGElement or HTMLElement
 * ```
 * const el1 = utils.getRefAsElement(this.$refs.myCheckboxInput)
 * const el2 = utils.getRefAsElement(this.$refs.myPathInSVG)
 *
 * if (el1 instanceof HTMLElement) {
 *   // Do something...
 * }
 *
 * if (el2 instanceof SVGElement) {
 *   // Do someting...
 * }
 * ```
 *
 * Note 2: This utility does not handle array refs properly, this is intentional to keep the return
 * type easy to work with.
 *
 * @example Example for dealing with array refs.
 * ```
 * const elements = this.$refs.listItems.map(utils.getRefAsElement)
 * ```
 *
 * @param input The ref picked up by your Vue component instance.
 * @returns A Element, or undefined if none can be found.
 */
export function getRefAsElement(input) {
    if (input === undefined) {
        return undefined;
    }
    if (input instanceof Element) {
        return input;
    }
    if (Array.isArray(input)) {
        return getRefAsElement(input[0]);
    }
    if (isVueComponent(input)) {
        return input.$el;
    }
    return undefined;
}
/**
 * Converts a Vue ref to an HTMLElement for DOM interaction.
 *
 * @param input The ref picked up by your Vue component instance.
 * @returns An HTMLElement, or undefined if none can be found.
 */
export function getRefAsHTMLElement(input) {
    const element = getRefAsElement(input);
    // HTMLElement extends Element, so this type guard ensures we have the right type.
    if (element instanceof HTMLElement) {
        return element;
    }
    return undefined;
}
